import { useLayoutEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import {
  ALLOCATION_VALUES_BY_COMPANY_DEFAULT_ERROR_MESSAGE,
  ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY,
} from 'common/constants/services/companies';
import { CompaniesService } from 'services';
import { throwError, toString } from 'utilities';
import {
  GetAllocationValuesByCompanyHook,
  GetAllocationValuesByCompanyParams,
  GetAllocationValuesByCompanyResponse,
  UseGetAllocationValuesByCompany,
} from './types';

const getAllocationValuesByCompany = async (params: GetAllocationValuesByCompanyParams) => {
  const { companyId, measurementDateId, version } = params;

  if (isNil(companyId) && isNil(measurementDateId)) return null;

  const companiesService = new CompaniesService();

  try {
    const allocationValues = (await companiesService.getAllocationValuesByCompanyId(
      toString(companyId),
      toString(measurementDateId),
      isNil(version) ? '' : toString(version)
    )) as GetAllocationValuesByCompanyResponse;

    if (!allocationValues) return throwError(ALLOCATION_VALUES_BY_COMPANY_DEFAULT_ERROR_MESSAGE);

    return allocationValues;
  } catch (error) {
    return throwError(ALLOCATION_VALUES_BY_COMPANY_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetAllocationValuesByCompany: UseGetAllocationValuesByCompany = params => {
  const { company, measurementDate, version, shouldQueryAutomatically = true } = params;

  const companyId = useMemo(() => company?.id, [company]);
  const measurementDateId = useMemo(() => measurementDate?.id, [measurementDate]);

  const committedMeasurementDateIdRef = useRef<number | undefined>(undefined);

  const versionForQuery = useMemo(() => {
    const committed = committedMeasurementDateIdRef.current;
    if (
      measurementDateId !== undefined &&
      measurementDateId !== null &&
      committed !== undefined &&
      committed !== null &&
      committed !== measurementDateId
    ) {
      return null;
    }
    return version;
  }, [measurementDateId, version]);

  useLayoutEffect(() => {
    committedMeasurementDateIdRef.current = measurementDateId ?? undefined;
  }, [measurementDateId]);

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetAllocationValuesByCompanyHook['data'],
    Error
  >({
    enabled: !isNil(companyId) && !isNil(measurementDateId) && shouldQueryAutomatically,
    queryFn: () =>
      getAllocationValuesByCompany({ companyId, measurementDateId, version: versionForQuery }),
    queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY, companyId, measurementDateId, versionForQuery],
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetAllocationValuesByCompany;
